// Route table (SITE_HOME.md, ACCOUNTS.md). The root is the front door for a
// visitor with no session and the hub for a signed-in user. League screens
// are scoped by league id so nothing shows the wrong league, and they need
// a session; a guest following a league link lands on the front door.

import { useEffect, useState, type ReactNode } from 'react';
import { valueOf, type Failure } from '@fsp/domain';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { EmptyState } from './kit';
import { Button } from './kit/ui';
import { useSession } from './data/session';
import { useSim, useTutorialLeague } from './data/SimProvider';
import { TutorialChapterProvider } from './data/tutorialChapter';
import { leagueIdFromPath } from './data/route';
import { isDemoLeague, useDemo } from './data/demo';
import { AppShell } from './shell/AppShell';
import { ChatDrawerProvider } from './shell/chatDrawer';
import { NotificationsProvider } from './data/notifications';
import { EditTeamProvider } from './shell/editTeam';
import { LeagueHatScope } from './shell/LeagueHatScope';
import { PlayerCardHost } from './shell/playerCard';
import { TutorialProvider } from './demo/TutorialProvider';
import { TutorialIndex } from './demo/TutorialIndex';
import { AccountScreen } from './screens/AccountScreen';
import { NotificationSettingsScreen } from './screens/NotificationSettingsScreen';
import { AchievementsScreen } from './screens/AchievementsScreen';
import { AppearanceAddress } from './shell/appearanceSheet';
import { AwardsScreen } from './screens/AwardsScreen';
import { DraftRecapScreen } from './screens/DraftRecapScreen';
import { DraftReplayScreen } from './screens/DraftReplayScreen';
import { DraftRoomScreen } from './screens/DraftRoomScreen';
import { DraftTvScreen } from './screens/DraftTvScreen';
import { HistoryScreen } from './screens/HistoryScreen';
import { KeepersScreen } from './screens/KeepersScreen';
import { LeagueScreen } from './screens/HomeScreen';
import { TeamPlace } from './screens/TeamPlace';
import { LeagueSection } from './screens/league/LeagueSection';
import { HubScreen } from './screens/HubScreen';
import { JoinScreen } from './screens/JoinScreen';
import { ResetScreen } from './screens/ResetScreen';
import { ConfirmEmailScreen } from './screens/ConfirmEmailScreen';
import { PickemScreen } from './screens/PickemScreen';
import { ChallengeScreen } from './screens/ChallengeScreen';
import { CheckInScreen } from './screens/CheckInScreen';
import { WorthALookScreen } from './screens/WorthALookScreen';
import { CalendarScreen } from './screens/CalendarScreen';
import { StopEmailScreen } from './screens/StopEmailScreen';
import { NoInvitesScreen } from './screens/NoInvitesScreen';
import { SignInLinkScreen } from './screens/SignInLinkScreen';
import { EmailChangeScreen } from './screens/EmailChangeScreen';
import { EmailUndoScreen } from './screens/EmailUndoScreen';
import { LeagueSetupScreen } from './screens/LeagueSetupScreen';
import { LineupScreen } from './screens/LineupScreen';
import { MatchupScreen } from './screens/MatchupScreen';
import { AuditScreen } from './screens/AuditScreen';
import { ManagersScreen } from './screens/ManagersScreen';
import { RecapScreen } from './screens/RecapScreen';
import { SeasonWrapScreen } from './screens/SeasonWrapScreen';
import { YearbookScreen } from './screens/YearbookScreen';
import { RulesScreen } from './screens/RulesScreen';
import { ScheduleScreen } from './screens/ScheduleScreen';
import { CompareScreen } from './screens/CompareScreen';
import { ScoreboardScreen } from './screens/ScoreboardScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { StandingsScreen } from './screens/StandingsScreen';
import { LabPlayerScreen } from './screens/LabPlayerScreen';
import { LabWeekScreen } from './screens/LabWeekScreen';
import { AboutScreen } from './screens/AboutScreen';
import { JOIN_REASON, START_REASON, WelcomeScreen } from './screens/WelcomeScreen';
import { LeaguePreDraftScreen } from './screens/LeaguePreDraftScreen';
import { MockDraftScreen } from './screens/MockDraftScreen';
import { SavedMockScreen } from './screens/SavedMockScreen';
import { LeagueMockScreen } from './screens/LeagueMockScreen';
import { PrioritiesScreen } from './screens/PrioritiesScreen';
import { PrivacyScreen } from './screens/PrivacyScreen';
import { CreditsScreen } from './screens/CreditsScreen';
import { inviteLine, useInvite } from './data/invite';
import { cardFor, useLeagues } from './data/LeaguesProvider';
import type { SignUpReason } from './screens/shared/SignUpSheet';
import { GoTo, RememberLeague } from './install/GoTo';
import { InstallScreen } from './install/InstallScreen';
import { InstalledReporter } from './install/InstalledReporter';

/**
 * The league screens that read a real league's own rows — every one (BACKLOG
 * E12, PLAN-2026-09-26-every-screen-and-crayon P5). Lives in
 * `data/connected.ts`; re-exported here for the route test's roll call.
 */
export { LEAGUE_SCREENS_ON_API } from './data/connected';

/** The league screens a league in setup opens as themselves, not as its pre-draft page. */
const BEFORE_THE_DRAFT: ReadonlySet<string> = new Set([
  'priorities',
  'rules',
  'settings',
  'draft',
  // A season started from the last has a past before its draft (H6), and
  // keepers to choose before it (Q7).
  'history',
  'keepers',
  // The schedule as it stands, which its commissioner can draw again (B-565).
  'schedule',
  // The calendar: the draft, keepers and votes before there are games (D12).
  'calendar',
]);

/**
 * `/lineup` was My Team's address before My Team was the league's own
 * (B-211). Links in notifications, the hub and old bookmarks still say it, so
 * it lands there, with any slot it asked for (`?slot=QB&i=0`).
 */
function ToMyTeam() {
  const { search } = useLocation();
  const { leagueId } = useParams();
  return <Navigate to={{ pathname: `/l/${leagueId ?? ''}`, search }} replace />;
}

/** The segment after `/l/<id>/`, or '' for the index. `matchup/:id` reads as `matchup`. */
export function tail(pathname: string): string {
  const rest = pathname.replace(/^\/l\/[^/]+\/?/, '');
  return rest.split('/')[0] ?? '';
}

/** Asking for the account page while signed out is a sign-IN, not a sign-up. */
const ACCOUNT_REASON: SignUpReason = {
  needs: 'That page is your account, so we need to know whose it is.',
  then: 'Sign in and you will land straight on it.',
};

/**
 * A link INTO a league, opened by somebody who is not signed in.
 *
 * Sign-in rather than sign-up, because a `/l/<id>/…` address is a page inside
 * a league and the people who have those links are already in it. Somebody
 * with no account followed an INVITE, which is `/join/<code>` and offers to
 * make them one.
 *
 * THIS IS THE THIRD CASE B-66 ALREADY PROMISED and nothing covered. *"Whether
 * you tapped a button, followed an invite link, or pasted a URL"* — a league
 * URL is the third, and it fell through to the general redirect and landed on
 * the front door with no explanation. D6 is what made it matter: the
 * `draft.finished` notification is a deep link to `/l/<id>/draft-recap` that
 * somebody taps on a phone the morning after their draft, which is exactly
 * the moment they are least likely to have a live session.
 */
const LEAGUE_REASON: SignUpReason = {
  needs: 'That page is inside a league.',
  then: 'Sign in and you will land back on it — we need to know which team is yours.',
};

/**
 * Nothing, for the moment before we know who is asking.
 *
 * WHY THIS EXISTS. Signing in is a cookie and a `GET /api/me` now, so on a
 * cold load there is a gap between *no session yet* and *the answer*. Almost
 * nobody sees it: the display identity is cached, so a returning visitor is
 * already signed in on the first paint (B-64). The exception is a browser
 * holding a valid COOKIE with no cache — localStorage cleared, privacy
 * settings blocking it, a second tab that signed in — and for them the
 * guards below would read "signed out" and **redirect with `replace`**,
 * which reconciling cannot undo. Found on 2026-09-14 by opening `/account`
 * in a browser whose cache had just been cleared: it bounced to the front
 * door and stayed there, signed in.
 *
 * So the guards wait instead of guessing. The wait is bounded: `reconciled`
 * becomes true even when the server cannot be reached, so an unreachable API
 * shows the front door rather than this.
 */
function Booting() {
  // Nothing for the first moment, because this is usually a few dozen
  // milliseconds and a flash of text would be noise. After that it says what
  // it is doing: this was `null` until 2026-09-24, and a server that did not
  // answer left the whole window empty until the request gave up (B-133).
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 1000);
    return () => clearTimeout(t);
  }, []);
  return slow ? (
    <div className="fsp-whole-window">
      <EmptyState>Connecting to the site…</EmptyState>
    </div>
  ) : null;
}

/** A read the whole route depends on did not come back, said in place rather than redirected. */
function NoAnswer({ failure, onRetry }: { failure: Failure; onRetry: () => void }) {
  return (
    <div className="fsp-whole-window">
      <EmptyState
        action={
          <Button size="xs" onClick={onRetry}>
            Try again
          </Button>
        }
      >
        {failure.message}
      </EmptyState>
    </div>
  );
}

/**
 * THE DEMO LEAGUE (O6, PLAN-2026-10-09-demo-league): open to anybody, signed
 * in or not, read from the API as any league is, at the Tuesday morning it is
 * held at. It comes before every guard in `Shell`, because each of those asks
 * whose league this is, and this one is nobody's: the server answers its
 * reads as the seat a visitor sits in, and refuses its writes.
 *
 * Not remembered as the league to open next (`RememberLeague`): a signed-in
 * person who looked around it still opens on their own.
 */
function DemoLeague() {
  const { snapshot, reload } = useDemo();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const demo = valueOf(snapshot);
  if (demo !== null) {
    // THE WEEKEND, ONCE (`DEMO_PATH`): `?weekend` becomes the week just
    // played, and leaves the address, so the stepper moves freely after it.
    const params = new URLSearchParams(search);
    if (params.has('weekend')) {
      params.delete('weekend');
      if (demo.week !== null) params.set('period', String(demo.week));
      const rest = params.toString();
      return <Navigate to={{ pathname, search: rest === '' ? '' : `?${rest}` }} replace />;
    }
    return (
      <AppShell>
        <Outlet />
      </AppShell>
    );
  }
  if (snapshot.state !== 'failed') return <Booting />;
  if (snapshot.failure.code === 'not_found') {
    return (
      <div className="fsp-whole-window">
        <EmptyState
          action={
            <Button size="xs" onClick={() => navigate('/')}>
              Back to the front page
            </Button>
          }
        >
          There is no demo league on this site yet.
        </EmptyState>
      </div>
    );
  }
  return <NoAnswer failure={snapshot.failure} onRetry={reload} />;
}

/**
 * A guest following an invite link signs up first and lands back here with the code kept (A3).
 *
 * EXPORTED FOR `App.guards.test.tsx` and for nothing else. The alternative is
 * a test that renders the whole app to reach one branch, which would be a
 * test of the provider stack; these three are the guards, and the guards are
 * what four BEHAVIORS entries say have gone wrong in front of Ken.
 */
export function JoinGate() {
  const { session, reconciled } = useSession();
  const { pathname, search } = useLocation();
  const { code } = useParams();
  // THE ONE PUBLIC READ, USED WHERE IT MATTERS MOST. `GET /api/invites/:code`
  // needs no account, and ICD-S01 §9 spends a paragraph justifying that: A3
  // begins with a link from a friend, and arriving at a bare account form
  // asks somebody to sign up to find out whether they want to sign up.
  //
  // It was built on 2026-09-14 and the front door did not call it. A stranger
  // opening a link got "Joining a league needs an account first" and nothing
  // about whose league — while [B-68](../../docs/design/BEHAVIORS.md) and
  // LEAGUE_SETUP.md both said the sheet named it. The hook is asked
  // unconditionally because hooks cannot be conditional; it does nothing at
  // all unless the code is eight valid characters.
  const { snapshot } = useInvite(code ?? '');
  // `ready` with a value, and nothing else. A lookup that is still running, a
  // code that names nothing, and a site we could not reach all mean the same
  // thing to this sheet: we have no league to name yet.
  const preview = snapshot.state === 'ready' ? snapshot.value : null;

  if (!session && !reconciled) return <Booting />;
  if (!session) {
    // Waiting on the preview would leave the person staring at a spinner on
    // the one screen that is meant to be welcoming, so the generic reason is
    // what shows until the answer lands — and a code that names nothing keeps
    // it, because "no such league" is `JoinScreen`'s sentence to say once
    // they are in, not a reason to refuse an account.
    // A team's link that no longer works says so before anybody signs up for
    // a team they cannot have (the review of J4).
    const reason =
      preview?.team && !preview.open
        ? {
            needs: 'That link no longer works. Ask whoever sent it for a new one.',
            then: 'You can still make an account, and a new link will work once you have one.',
          }
        : preview
          ? {
              needs: inviteLine(preview),
              then:
                preview.team !== null
                  ? 'Taking over needs an account — the league has to know who runs the team. ' +
                    'After this the team is yours.'
                  : 'Joining needs an account — the league has to know who you are, and your team has ' +
                    "to belong to someone. After this you'll name your team and you're in.",
            }
          : JOIN_REASON;
    // THE INVITE AN EMAILED LINK CARRIES (`?invite=`) comes back with them
    // from signing up, so the join ends it (PLAN-2026-10-28-by-email §3.1).
    return <WelcomeScreen initialSheet="sign-up" next={`${pathname}${search}`} reason={reason} />;
  }
  return (
    <AppShell>
      <JoinScreen />
    </AppShell>
  );
}

export function Shell({ bare = false }: { bare?: boolean } = {}) {
  const { session, reconciled } = useSession();
  const { pathname } = useLocation();
  const { world, tutorial } = useSim();
  const real = useLeagues();
  const leagueId = leagueIdFromPath(pathname);
  const tutorialLeague = useTutorialLeague(leagueId);
  // BARE IS THE SAME DOOR WITHOUT THE FRAME: the draft on a TV (M3) takes
  // the whole window, and every check below is still its own (the review of
  // 2026-11-01: a door of its own let a league one is not in show the
  // practice league's draft under its address).
  const framed = (content: ReactNode) => (bare ? <>{content}</> : <AppShell>{content}</AppShell>);

  // Every redirect below is `replace`, so a wrong one is permanent for that
  // navigation. None of them may run on a guess — see `Booting`.
  if (!session && !reconciled && !tutorial) return <Booting />;

  // The demo league is anybody's to look around, and nobody's to own (O6).
  // Its draft is over and is not shown on a TV: the room's own page has it.
  if (isDemoLeague(leagueId))
    return bare ? <Navigate to={`/l/${leagueId}/draft`} replace /> : <DemoLeague />;

  // An account is required to own anything, a league above all. Nothing in the
  // tutorial is saved, so a league made there would take its members, its
  // rules and its invite code with it, and the invite link would point at
  // nothing.
  //
  // It used to `Navigate` away, and that was wrong in a way only a walk
  // shows: a visitor in the tutorial who opens the Leagues menu and picks
  // "Start a league" was **silently dumped on the front door** — no sheet,
  // no message, tutorial lost. They had just told us exactly what they
  // wanted. Bouncing them is the same discourtesy as an unexplained sign-up
  // popup, from the other direction.
  //
  // So it offers the door instead, with the reason and the destination kept,
  // which is what the invite link has always done. Answering the same
  // question two different ways was its own bug.
  //
  // THESE TWO COME BEFORE THE GENERAL REDIRECT, and did not until 2026-09-20.
  // Below it they were unreachable for anybody who was not in the tutorial,
  // because the line under this comment fires on `!session && !tutorial`
  // first — so a visitor who pasted `/account`, or whose session expired and
  // who reloaded it, was dumped on the front door with no explanation, and
  // `ACCOUNT_REASON` was a sentence nothing could show. B-66 says the sheet
  // arrives where you asked for it "whether you tapped a button, followed an
  // invite link, or pasted a URL", and for a day and a half that was true of
  // two thirds of the list. The first render test of this file found it
  // (AUDIT-2026-09-19 F9).
  if (!session && pathname === '/leagues/new')
    return <WelcomeScreen initialSheet="sign-up" next="/leagues/new" reason={START_REASON} />;
  if (!session && pathname === '/account')
    return <WelcomeScreen initialSheet="sign-in" next="/account" reason={ACCOUNT_REASON} />;

  // AND A LEAGUE URL IS THE THIRD CASE, which is the one B-66's own sentence
  // names and nothing covered until 2026-09-22. Somebody who follows the link
  // in a `draft.finished` push the morning after their draft, or who opens
  // one of these on a phone that signed out a week ago, was dropped on the
  // marketing front door — no sheet, no reason, and no way to tell whether
  // the link was broken or they were.
  if (!session && !tutorial && leagueId)
    return <WelcomeScreen initialSheet="sign-in" next={pathname} reason={LEAGUE_REASON} />;

  // The tutorial is a mode, not an account (DEMO.md), so a visitor walking a
  // chapter gets the shell with no session. Everything else still needs one,
  // and the front door is the answer for every path that has no better one.
  if (!session && !tutorial) return <Navigate to="/" replace />;

  // Nothing below may decide about a league before the REAL ones have
  // arrived. On a cold load straight to `/l/<id>` they have not, and every
  // check after this reads as "no such league" — which is how a league
  // somebody really is in bounced them to the hub. Same lesson as `Booting`
  // above; this file simply has two sources of truth to wait for now.
  // A REFRESH IN FLIGHT COUNTS AS NOT ARRIVED, and that is not pedantry. The
  // one moment somebody lands on a league route that the hub has never heard
  // of is the moment they have just made or joined it: the wizard calls
  // `reload()` and navigates in the same breath. A reload keeps the leagues it
  // already had on screen rather than blanking the hub (ICD-U01 §4), so the
  // answer is `ready` and `stale` while the new league is still on its way,
  // and the guard below would read "no such league" and bounce them off the
  // page they just created. Caught by a headless walk on 2026-09-17, which is
  // the third time this file has learned the same lesson.
  const settled =
    real.snapshot.state !== 'loading' && !(real.snapshot.state === 'ready' && real.snapshot.stale);
  if (leagueId && !settled) return <Booting />;

  // A TUTORIAL CHAPTER'S LEAGUE IS THE SIMULATOR'S while the chapter is open
  // (B-527): a local database seeded from its world holds real leagues under
  // the same ids, and the chapter read those instead.
  const realCard = tutorialLeague ? null : cardFor(real.snapshot, leagueId);

  // A HUB THAT FAILED IS NOT "YOU ARE NOT IN THIS LEAGUE". The guard below
  // redirects a league the hub does not list; a hub that timed out lists
  // nothing it did not already hold, and redirecting then threw away the
  // league somebody was opening — or had just made — over a server that did
  // not answer. Say so here, with Try again, and keep the address (B-133).
  if (
    session &&
    !session.demo &&
    !tutorial &&
    leagueId &&
    !realCard &&
    real.snapshot.state === 'failed'
  ) {
    return <NoAnswer failure={real.snapshot.failure} onRetry={real.reload} />;
  }

  // A league I really am in: its own screens, never a bounce to the hub —
  // joining with a code writes a real seat and the hub shows the card, so
  // silently returning somebody there when they open it is the discourtesy
  // the guards above used to commit.
  //
  // THE `!session?.demo` IS LOAD-BEARING, and the reason is a collision
  // nobody designed: `db:seed:local` generated the fixture FROM the
  // simulator, so the real leagues carry the simulator's ids — `family` is
  // both Robin's real league and the practice one. Without this, opening a
  // real league silently rendered the SIMULATED league of the same id, from
  // the protagonist's point of view. Robin would have been looking at Ken's
  // practice team and believing it was his.
  //
  // So: asked to look around, you get the example league (the bar says so).
  // Otherwise a real league is a real league.
  if (realCard && (!session?.demo || !world.some((s) => s.leagueId === leagueId && s.joined))) {
    // EVERY LEAGUE SCREEN READS A REAL LEAGUE'S OWN ROWS (E12, and since
    // PLAN-2026-09-26-every-screen-and-crayon P5 all of them), so a real
    // league is never shown the simulator's numbers under its name. The list
    // in `data/connected.ts` is the roll call `routes.test.ts` holds to the
    // routes below.
    const screen = tail(pathname);
    // A LEAGUE IN SETUP HAS ITS OWN PAGE, and five more that belong before a
    // draft: the priorities, where a manager gets ready for it (PLAN-2026-09-24
    // item B); the rules and the settings, where a commissioner changes them
    // before anybody has drafted (P2); the draft's own lobby (P5); and the
    // history a season started from the last already has (H6). Every other
    // path renders the pre-draft screen.
    if (realCard.league.status === 'setup' && !BEFORE_THE_DRAFT.has(screen)) {
      return framed(
        <>
          <RememberLeague id={realCard.league.id} />
          {/* A league that has not drafted has real things to show — who is
              in, which seats are open, the code — and they are the only
              things a commissioner who just made one wants. */}
          <LeaguePreDraftScreen leagueId={realCard.league.id} />
        </>,
      );
    }
    // A REAL LEAGUE ON A CONNECTED SCREEN IS SHOWN, and says so here rather
    // than falling through to the simulator's checks below. It used to fall
    // through, and the last of them asks whether the SIMULATOR has a league
    // with this id — which every seeded league does, by the accident this
    // block's first comment describes, and which a league made through the
    // form (`lg-…`) never does. So the morning after its draft, a new league's
    // Home bounced its own commissioner to the hub (found 2026-09-24).
    return framed(
      <>
        <RememberLeague id={realCard.league.id} />
        <Outlet />
      </>,
    );
  }

  // A signed-in manager with no demo open has no SIMULATED leagues to show.
  //
  // `&& !realCard` was added on 2026-09-14 and the guard was wrong without
  // it: it encoded "signed in, demo off, therefore you have no leagues",
  // which was true while every league was simulated and stopped being true
  // the moment joining with a code wrote a real seat. Robin joined Family
  // Cup for real, saw the card on the hub, tapped it, and landed back on the
  // hub with no explanation — this line.
  if (session && !session.demo && !tutorial && leagueId && !realCard)
    return <Navigate to="/" replace />;

  // A league that does not exist (or that I am not in) is never shown as some other league.
  if (leagueId && !world.some((s) => s.leagueId === leagueId && s.joined))
    return <Navigate to="/" replace />;
  return framed(<Outlet />);
}

/**
 * `/` is the front door whenever there is no account — always, whatever the
 * visitor was in the middle of. That one rule is what makes it impossible to
 * get stuck anywhere in the tutorial: the way out is the thing they already
 * know.
 */
export function Root() {
  const { session, reconciled } = useSession();
  // No redirect here, so a wrong guess is only a flash rather than a trap —
  // but the front door and the hub are different enough that showing the
  // wrong one for a moment still reads as a bug.
  if (!session && !reconciled) return <Booting />;
  return session ? (
    <AppShell>
      <HubScreen />
    </AppShell>
  ) : (
    <WelcomeScreen />
  );
}

export function App() {
  return (
    <TutorialChapterProvider>
      <NotificationsProvider>
        <InstalledReporter />
        <LeagueHatScope>
          <ChatDrawerProvider>
            <TutorialProvider>
              <EditTeamProvider>
                <Routes>
                  <Route path="/" element={<Root />} />
                  <Route path="/welcome" element={<WelcomeScreen />} />
                  <Route path="/join" element={<JoinGate />} />
                  <Route path="/join/:code" element={<JoinGate />} />
                  {/* The link in a reset email (B-306): signed in or out, the same screen. */}
                  <Route path="/reset" element={<ResetScreen />} />
                  {/* The links in email (PLAN-2026-10-18 §7.1): signed in or out, the same screen. */}
                  <Route path="/confirm-email" element={<ConfirmEmailScreen />} />
                  <Route path="/stop-email" element={<StopEmailScreen />} />
                  <Route path="/no-invites" element={<NoInvitesScreen />} />
                  {/* The link in a sign-in code's email (PLAN-2026-10-28-by-email §3.4). */}
                  <Route path="/sign-in" element={<SignInLinkScreen />} />
                  {/* A change of sign-in address's two links (§3.5). */}
                  <Route path="/email-change" element={<EmailChangeScreen />} />
                  <Route path="/email-undo" element={<EmailUndoScreen />} />
                  {/*
            A development surface, not a screen anybody navigates to. It is a
            sibling of the front door rather than a child of <Shell> on purpose:
            Shell redirects anyone without a session to "/", which would make
            this unusable for exactly the bare check it exists for. Not in the
            nav rail, not in DESIGN_PLAN, and named in the handoff as a lab
            route so it cannot quietly become an undesigned screen.
          */}
                  <Route path="/lab/player/:id" element={<LabPlayerScreen />} />
                  <Route path="/lab/week" element={<LabWeekScreen />} />
                  {/* ALSO OUTSIDE `Shell`, and for its reason. `Shell` redirects anyone
              without a session, and "did this deploy land" is a question you
              most need answered on a deploy you cannot sign in to. */}
                  <Route path="/about" element={<AboutScreen />} />
                  {/* Read before an account exists, so outside `Shell` too (B-417). */}
                  <Route path="/privacy" element={<PrivacyScreen />} />
                  {/* And so are the credits, read from the front page's foot (B-592). */}
                  <Route path="/credits" element={<CreditsScreen />} />
                  <Route path="/go/:where" element={<GoTo />} />
                  {/* THE DRAFT ON A TV (M3, B-608): the whole window, so outside
              `Shell`'s frame, and through its door. */}
                  <Route element={<Shell bare />}>
                    <Route path="/l/:leagueId/draft/tv" element={<DraftTvScreen />} />
                  </Route>
                  <Route element={<Shell />}>
                    <Route path="/account" element={<AccountScreen />} />
                    <Route path="/account/notifications" element={<NotificationSettingsScreen />} />
                    <Route path="/account/achievements" element={<AchievementsScreen />} />
                    {/* A sheet over the screen it was opened from (B-399). */}
                    <Route path="/account/appearance" element={<AppearanceAddress />} />
                    <Route path="/account/install" element={<InstallScreen />} />
                    {/* Appearance's address before it was an account setting (B-214). */}
                    <Route
                      path="/appearance"
                      element={<Navigate to="/account/appearance" replace />}
                    />
                    <Route path="/demo" element={<TutorialIndex />} />
                    {/* The league-cards view the tutorial's first chapter opens on,
                 showing the tour's leagues and nothing else (`useHub`). `/`
                 is the front door for a visitor, so it cannot live there. */}
                    <Route path="/demo/leagues" element={<HubScreen tour />} />
                    <Route path="/leagues/new" element={<LeagueSetupScreen />} />
                    {/* One player card for every name in a league (B-220). */}
                    <Route path="/l/:leagueId" element={<PlayerCardHost />}>
                      {/* My Team and its tools, one place that changes shape (B-400). */}
                      <Route index element={<TeamPlace />} />
                      <Route path="league" element={<LeagueScreen />} />
                      {/* THE LEAGUE'S SECTIONS (B-403): each grows out of its panel on
                      the front page, and Back returns to it. */}
                      <Route
                        path="rules"
                        element={
                          <LeagueSection>
                            <RulesScreen />
                          </LeagueSection>
                        }
                      />
                      <Route path="settings" element={<SettingsScreen />} />
                      {/* The commissioner's scoring tool, kept out of the way: the
                      table everybody reads is the rules page's and Settings'
                      (PLAN-2026-10-18 §2.5). */}
                      <Route path="settings/scoring" element={<SettingsScreen tool="scoring" />} />
                      <Route path="draft" element={<DraftRoomScreen />} />
                      <Route path="draft/mock" element={<MockDraftScreen />} />
                      <Route path="draft/mocks/:mockId" element={<SavedMockScreen />} />
                      <Route path="draft/together" element={<LeagueMockScreen />} />
                      {/* The finished draft played back, the morning after (C11). */}
                      <Route path="draft/replay" element={<DraftReplayScreen />} />
                      <Route
                        path="draft-recap"
                        element={
                          <LeagueSection>
                            <DraftRecapScreen />
                          </LeagueSection>
                        }
                      />
                      <Route path="priorities" element={<PrioritiesScreen />} />
                      <Route path="lineup" element={<ToMyTeam />} />
                      <Route path="teams/:teamId" element={<LineupScreen />} />
                      <Route path="matchup" element={<MatchupScreen />} />
                      {/* The calendar (D12, PLAN-2026-10-30-the-calendar): a place of its own. */}
                      <Route path="calendar" element={<CalendarScreen />} />
                      <Route path="matchup/:id" element={<MatchupScreen />} />
                      <Route
                        path="scoreboard"
                        element={
                          <LeagueSection>
                            <ScoreboardScreen />
                          </LeagueSection>
                        }
                      />
                      <Route
                        path="standings"
                        element={
                          <LeagueSection>
                            <StandingsScreen />
                          </LeagueSection>
                        }
                      />
                      {/* Pick'em (scenario I6, B-447): the league's games, for pride. */}
                      <Route
                        path="pickem"
                        element={
                          <LeagueSection>
                            <PickemScreen />
                          </LeagueSection>
                        }
                      />
                      {/* The quiet manager's check-in (J3, PLAN-2026-11-01-the-quiet-manager-check-in): where its notice opens. */}
                      <Route
                        path="check-in"
                        element={
                          <LeagueSection>
                            <CheckInScreen />
                          </LeagueSection>
                        }
                      />
                      {/* The weekly challenge (Q9, PLAN-2026-11-01-the-weekly-challenge): the site's week, from a league. */}
                      <Route
                        path="challenge"
                        element={
                          <LeagueSection>
                            <ChallengeScreen />
                          </LeagueSection>
                        }
                      />
                      {/* Worth a look (E1, PLAN-2026-10-27-worth-a-look): who on no team has scored lately. */}
                      <Route
                        path="worth-a-look"
                        element={
                          <LeagueSection>
                            <WorthALookScreen />
                          </LeagueSection>
                        }
                      />
                      <Route
                        path="schedule"
                        element={
                          <LeagueSection>
                            <ScheduleScreen />
                          </LeagueSection>
                        }
                      />
                      <Route path="plan" element={<TeamPlace tool="plan" />} />
                      <Route path="compare" element={<CompareScreen />} />
                      <Route
                        path="awards"
                        element={
                          <LeagueSection>
                            <AwardsScreen />
                          </LeagueSection>
                        }
                      />
                      <Route
                        path="history"
                        element={
                          <LeagueSection>
                            <HistoryScreen />
                          </LeagueSection>
                        }
                      />
                      <Route
                        path="keepers"
                        element={
                          <LeagueSection>
                            <KeepersScreen />
                          </LeagueSection>
                        }
                      />
                      <Route
                        path="recap"
                        element={
                          <LeagueSection>
                            <RecapScreen />
                          </LeagueSection>
                        }
                      />
                      {/* A team's season wrapped (H9), once it is over. */}
                      <Route
                        path="wrapped"
                        element={
                          <LeagueSection>
                            <SeasonWrapScreen />
                          </LeagueSection>
                        }
                      />
                      {/* The league's yearbook (H9), once the champion is crowned. */}
                      <Route
                        path="yearbook"
                        element={
                          <LeagueSection>
                            <YearbookScreen />
                          </LeagueSection>
                        }
                      />
                      <Route path="players" element={<TeamPlace tool="players" />} />
                      <Route path="news" element={<TeamPlace tool="news" />} />
                      <Route
                        path="audit"
                        element={
                          <LeagueSection>
                            <AuditScreen />
                          </LeagueSection>
                        }
                      />
                      <Route
                        path="managers"
                        element={
                          <LeagueSection>
                            <ManagersScreen />
                          </LeagueSection>
                        }
                      />
                      <Route path="waivers" element={<TeamPlace tool="waivers" />} />
                      <Route path="trades" element={<TeamPlace tool="trades" />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
              </EditTeamProvider>
            </TutorialProvider>
          </ChatDrawerProvider>
        </LeagueHatScope>
      </NotificationsProvider>
    </TutorialChapterProvider>
  );
}
